import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

@customElement('app-footer')
export class AppFooter extends LitElement {
  @state() showShortcuts = false;
  @state() isDarkMode = false;

  connectedCallback() {
    super.connectedCallback();
    // Check saved preference first, then system preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      this.isDarkMode = savedTheme === 'dark';
      document.documentElement.setAttribute('data-theme', savedTheme);
    } else {
      this.isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
  }

  toggleTheme() {
    this.isDarkMode = !this.isDarkMode;
    const theme = this.isDarkMode ? 'dark' : 'light';
    // Apply theme by setting data attribute on html element
    document.documentElement.setAttribute('data-theme', theme);
    // Save preference to localStorage
    localStorage.setItem('theme', theme);
  }
  static styles = css`
    :host {
      display: block;
      background: var(--background);
      border-top: 1px solid var(--border);
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      height: 36px;
      z-index: 100; /* Above sidebar (z-index: 10) */
    }

    footer {
      font-family: var(--font-mono);
      padding: 0 calc(var(--spacing) * 4);
      margin: 0;
      text-align: left;
      font-size: 0.85rem;
      color: var(--muted-foreground);
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 100%;
      gap: calc(var(--spacing) * 5); /* 20px using spacing unit */
    }

    .footer-right {
      display: flex;
      align-items: center;
      gap: calc(var(--spacing) * 3); /* 12px using spacing unit */
    }

    .footer-links {
      display: flex;
      gap: calc(var(--spacing) * 5); /* 20px using spacing unit */
      justify-content: flex-start;
      align-items: center;
      flex-wrap: wrap;
    }

    .version {
      font-size: 0.75rem;
      color: var(--muted-foreground);
      opacity: 0.7;
    }

    .shortcuts-trigger {
      position: relative;
      cursor: help;
      color: var(--muted-foreground);
      font-size: 1rem;
      padding: 8px;
      transition: color 0.2s ease, transform 0.2s ease;
      line-height: 1;
      display: flex;
      align-items: center;
    }

    .shortcuts-trigger:hover {
      color: var(--foreground);
      transform: scale(1.1);
    }

    .shortcuts-popup {
      position: fixed;
      bottom: 50px;
      left: auto;
      right: 120px;
      background: var(--card);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 12px 16px;
      font-size: 0.8rem;
      width: 220px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      display: none;
    }

    .shortcuts-trigger:hover .shortcuts-popup {
      display: block;
    }

    .shortcuts-popup h4 {
      margin: 0 0 10px 0;
      font-size: 0.85rem;
      font-weight: 600;
      color: var(--foreground);
      padding-bottom: 8px;
      border-bottom: 1px solid var(--border);
    }

    .shortcut-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      gap: 16px;
      white-space: nowrap;
    }

    .shortcut-item:last-child {
      margin-bottom: 0;
    }

    .shortcut-label {
      color: var(--muted-foreground);
    }

    .shortcut-key {
      background: var(--accent);
      border: 1px solid var(--border);
      padding: 2px 6px;
      border-radius: 3px;
      font-family: var(--font-mono);
      font-size: 0.75rem;
      color: var(--foreground);
      font-weight: 600;
    }

    .footer-links a {
      color: var(--muted-foreground);
      text-decoration: none;
      transition: color 0.2s ease;
    }

    .footer-links a:hover {
      color: var(--foreground);
    }

    .theme-toggle {
      background: transparent;
      border: 1px solid var(--border);
      color: var(--foreground);
      cursor: pointer;
      padding: 6px 12px;
      border-radius: var(--radius);
      font-family: var(--font-mono);
      font-size: 0.85rem;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .theme-toggle:hover {
      border-color: var(--primary);
      background: var(--accent);
    }

    @media (max-width: 768px) {
      footer {
        padding: 0 calc(var(--spacing) * 2); /* Minimal horizontal padding on mobile */
        margin: calc(var(--spacing) * 2.5) 0; /* Minimal vertical margin on mobile (10px) */
        font-size: 0.8rem;
        flex-direction: column;
        align-items: flex-start;
        gap: calc(var(--spacing) * 2.5); /* 10px using spacing unit */
      }

      .footer-links {
        gap: calc(var(--spacing) * 4); /* 16px using spacing unit */
      }

      .footer-right {
        gap: calc(var(--spacing) * 2); /* 8px using spacing unit */
      }

      .version {
        font-size: 0.7rem;
      }

      .shortcuts-trigger {
        display: none;
      }
    }
  `;

  render() {
    return html`
      <footer>
        <div class="footer-links">
          <a href="#">عن المنصة</a>
          <a href="#">الشروط والأحكام</a>
          <a href="#">تواصل معنا</a>
        </div>
        <div class="footer-right">
          <button class="theme-toggle" @click=${this.toggleTheme}>
            ${this.isDarkMode ? '☀️' : '🌙'}
            ${this.isDarkMode ? 'وضع فاتح' : 'وضع داكن'}
          </button>
          <div class="shortcuts-trigger">
            ⌨️
            <div class="shortcuts-popup">
              <h4>اختصارات لوحة المفاتيح</h4>
              <div class="shortcut-item">
                <span class="shortcut-label">فئة جديدة</span>
                <span class="shortcut-key">N</span>
              </div>
              <div class="shortcut-item">
                <span class="shortcut-label">أداة الفحص</span>
                <span class="shortcut-key">Alt+X+C</span>
              </div>
            </div>
          </div>
          <div class="version">v3.2.0</div>
        </div>
      </footer>
    `;
  }
}
